'use client';

import BannerSection from '@/components/common/bottom-banner/bottom-banner-section';
import CommonButton from '@/components/common/common-button/common-button';
import CaseStudyMeta from "@/components/common/seo/case-study-meta";
import DesignThinkingProcess from '@/components/ui/custom/stepper/DesignThinkingProcess';
import { caseStudies } from "@/lib/dto/case-studies/case-studies";
import {
    DailyStatusUpdates,
    GlowyAddPrivateRoutine,
    GlowyAddPrivateRoutineRecur,
    GlowyAddPrivateRoutineSteps,
    GlowyAnalytics,
    GlowyClearSkin,
    GlowyCreatorListing,
    GlowyCreatorStats,
    GlowyDesignBg,
    GlowyEditStep,
    GlowyForAllSkin,
    GlowyHeroMockup,
    GlowyHomeScreen,
    GlowyHomeScreen2,
    GlowyInconsistentRoutine,
    GlowyLogin,
    GlowyProblemBg,
    GlowyProblemMockup,
    GlowyReports,
    GlowyRoutineActivity,
    GlowyScreen1,
    GlowyScreen10,
    GlowyScreen11,
    GlowyScreen12,
    GlowyScreen2,
    GlowyScreen3,
    GlowyScreen4,
    GlowyScreen5,
    GlowyScreen6,
    GlowyScreen7,
    GlowyScreen8,
    GlowyScreen9,
    GlowySignup,
    GlowySkinconcern,
    GlowySkintype,
    GlowySplash,
    GlowyTanvi,
    GlowyWireframe1,
    GlowyWireframe2,
    GlowyWireframe3,
    TrelloCard
} from '@/lib/images';
import { motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';

// Reusable Timeline Card Component
const TimelineCard = ({
    color,
    title,
    description,
    position
}: {
    color: string;
    title: string;
    description: string;
    position: string;
}) => {
    return (
        <div className={`absolute ${position} w-[200px] z-10 group`}>
            <div className={`${color} text-white px-6 py-4 rounded-xl shadow-md relative overflow-hidden`}>
                <div className="flex items-center mb-2 relative z-10">
                    <span className="font-semibold">
                        {title}
                    </span>
                    <div className="w-2 h-2 bg-white rounded-full ml-2"></div>
                </div>
            </div>
            <div className="bg-white p-4 rounded-xl shadow-md mt-1 relative overflow-hidden">
                <p className="text-sm text-gray-700 relative z-10">{description}</p>
            </div>
        </div>
    );
};

// Phone Screenshot Component
const PhoneScreenshot = ({ src, alt }: { src: string, alt: string }) => {
    return (
        <div className="relative will-change-transform">
            <div className="relative w-[180px] sm:w-[220px] lg:w-[260px] rounded-[30px]">
                <div className="rounded-lg shadow-lg overflow-hidden">
                    <Image
                        src={src}
                        alt={alt}
                        width={300}
                        height={600}
                        className="w-full h-auto"
                    />
                </div>

                {/* Shadow beneath phone */}
                <div className="absolute -bottom-4 left-0 right-0 h-4 bg-black/10 blur-md rounded-full mx-4"></div>
            </div>
        </div>
    );
};

// Glowy app case study page
export default function GlowyAppCaseStudy() {
    // Find the case study data for this page
    const caseStudy = caseStudies.find(cs => cs.link === "/case-studies/skincare-routine-mobile-app");

    return (
        <>
            {caseStudy && <CaseStudyMeta caseStudy={caseStudy} />}
            {/* Hero Section */}
            <div className="relative w-full min-h-screen overflow-hidden">
                {/* Background with gradient and pattern */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFF6F8] to-[#FFF0F2]"></div>

                {/* Abstract shapes */}
                <div className="absolute top-[5%] right-[5%] w-[400px] h-[400px] rounded-full bg-[#FF8B75]/5 blur-[50px]"></div>
                <div className="absolute bottom-[5%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[40px]"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[15%] w-[200px] h-[200px] rounded-full border border-[#FF8B75]/10"></div>
                <div className="absolute bottom-[20%] right-[10%] w-[150px] h-[150px] rounded-full border border-[#FF8B75]/10"></div>

                {/* Glow effects */}
                <div className="absolute top-[30%] left-[10%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/10 blur-[100px]"></div>
                <div className="absolute bottom-[10%] right-[15%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[80px]"></div>

                {/* Content container - improved spacing */}
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl relative z-10 pt-28 md:pt-44 lg:pt-52 pb-12 md:pb-20 flex flex-col md:flex-col lg:flex-row items-center">
                    {/* Left side - Text content */}
                    <div className="w-full lg:w-3/2 mb-8 lg:mb-0 text-center md:text-center lg:text-left">
                        <div className="mb-6">
                            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
                                <span className="block">Glowy</span>
                                <span className="block mt-2 md:mt-4 text-transparent bg-clip-text bg-gradient-to-r from-[#FF8B75] to-[#FF6F54]">
                                    Skincare App
                                </span>
                            </h1>
                        </div>

                        <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-8 md:mb-10 max-w-lg mx-auto lg:mx-0">
                            A personalized skincare companion that helps users build routines, track progress, and achieve healthier skin.
                        </p>

                        {/* Tags */}
                        <div className="flex flex-wrap gap-3 justify-center lg:justify-start mb-10">
                            <div className="px-5 py-2 bg-white rounded-full text-[#FF8B75] font-medium shadow-sm shadow-[#FF8B75]/10">
                                Mobile App
                            </div>

                            <div className="px-5 py-2 bg-white rounded-full text-[#FF8B75] font-medium shadow-sm shadow-[#FF8B75]/10">
                                UX/UI Case Study
                            </div>

                            <div className="px-5 py-2 bg-white rounded-full text-gray-700 font-medium shadow-lg shadow-[#FF8B75]/10 border border-[#FF8B75]/10">
                                2025
                            </div>
                        </div>

                        {/* Scroll indicator */}
                        <div className="hidden lg:flex items-center mt-16 text-gray-400 text-sm">
                            <div className="w-6 h-[1px] bg-gray-300 mr-2"></div>
                            Scroll to explore
                        </div>
                    </div>

                    {/* Right side - Hero Image */}
                    <div className="w-full relative h-[400px] sm:h-[450px] md:h-[500px] scale-120 lg:h-[600px]">
                        <div className="relative w-full h-full">
                            {/* Phone mockup */}
                            <div className="relative z-10 mt-8 scale-110 md:mt-0 w-full h-full">
                                <Image
                                    src={GlowyHeroMockup.src}
                                    alt="Glowy App Mockup"
                                    fill
                                    className="object-contain"
                                    priority
                                />
                            </div>

                            {/* Reflection effect */}
                            <div className="absolute bottom-[-5%] left-[20%] right-[5%] h-[100px] bg-gradient-to-t from-white/30 to-transparent rounded-[50%] blur-2xl"></div>
                        </div>

                    </div>
                </div>
            </div>

            {/* Personal Space Section - Shortened for better performance */}
            <div className="bg-gradient-to-b from-[#F8FAFF] to-[#F0F6FF] py-12 md:py-16">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="flex flex-col items-center">
                        <div className="overflow-hidden">
                            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-center mb-10 md:mb-16 max-w-5xl">
                                <div className="sm:hidden">
                                    {/* Mobile-only version with better text breaking */}
                                    <span className="inline-block text-gray-800 pr-1">
                                        Your{" "}
                                    </span>
                                    <span className="inline-block text-[#8B9DC7] pr-1">
                                        personal space{" "}
                                    </span>
                                    <span className="inline-block text-gray-800 pr-1">
                                        for
                                    </span>
                                    <div className="my-2"></div>
                                    <span className="inline-block text-gray-800 pr-1">
                                        skincare{" "}
                                    </span>
                                    <span className="relative inline-flex items-center px-1">
                                        <span className="relative z-10">routines</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform -rotate-1"></span>
                                        <span className="ml-2">🌿</span>
                                    </span>
                                    <span className="inline-block text-gray-800 pr-1">
                                        ,
                                    </span>
                                    <div className="my-2"></div>
                                    <span className="relative inline-flex items-center px-1">
                                        <span className="relative z-10">healthy habits</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform rotate-1"></span>
                                        <span className="ml-2">💧</span>
                                    </span>
                                    <span className="inline-block text-gray-800 pr-1">
                                        ,
                                    </span>
                                    <div className="my-2"></div>
                                    <span className="inline-block text-gray-800 pr-1">
                                        and{" "}
                                    </span>
                                    <span className="relative inline-flex items-center px-2">
                                        <span className="relative z-10">glowing skin</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform -rotate-1"></span>
                                        <span className="ml-2">✨</span>
                                    </span>
                                </div>

                                <div className="hidden sm:block">
                                    {/* Tablet and desktop version */}
                                    <span className="inline-block text-gray-800 pr-2">
                                        {"Your "}
                                    </span>
                                    <span className="inline-block text-[#8B9DC7] pr-2">
                                        personal space{" "}
                                    </span>
                                    <span className="inline-block text-gray-800 pr-2">
                                        for
                                    </span>
                                    <br />
                                    <span className="inline-block text-gray-800 pr-2">
                                        skincare{" "}
                                    </span>
                                    <span className="relative inline-flex items-center px-2 pr-2">
                                        <span className="relative z-10">routines</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform -rotate-1"></span>
                                        <span className="ml-2">🌿</span>
                                    </span>
                                    <span className="inline-block text-gray-800 pr-2">
                                        ,
                                    </span>
                                    <span className="relative inline-flex items-center px-2 mx-2 pr-2">
                                        <span className="relative z-10">healthy habits</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform rotate-1"></span>
                                        <span className="ml-2">💧</span>
                                    </span>
                                    <span className="inline-block text-gray-800 pr-2">
                                        ,
                                    </span>
                                    <br />
                                    <span className="inline-block text-gray-800 pr-2">
                                        and{" "}
                                    </span>
                                    <span className="relative inline-flex items-center px-2 pr-2">
                                        <span className="relative z-10">glowing skin</span>
                                        <span className="absolute inset-0 bg-[#fce9e5] rounded-full opacity-50 transform -rotate-1"></span>
                                        <span className="ml-2">✨</span>
                                    </span>
                                </div>
                            </h2>
                        </div>
                    </div>
                </div>
            </div>

            {/* About the Project Section */}
            <div className="bg-[#FFF3ED] py-32">
                <div className="container mx-auto px-4 max-w-6xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 mb-20">
                        {/* Left side - Heading */}
                        <div>
                            <div className="overflow-hidden">
                                <h2 className="text-5xl md:text-6xl font-bold text-[#34495E] leading-tight">
                                    About<br />the project
                                </h2>
                            </div>
                        </div>

                        {/* Right side - Description */}
                        <div className="flex items-center">
                            <div className="overflow-hidden">
                                <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                                    Glowy is a skincare app development project that helps users manage routines, enhance skin health, and build lasting self-care habits. Some of its key features include:
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Feature Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {/* Card 1 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M14.5 4.5C14.5 3.12 13.38 2 12 2C10.62 2 9.5 3.12 9.5 4.5V6H7C5.9 6 5 6.9 5 8V19C5 20.1 5.9 21 7 21H17C18.1 21 19 20.1 19 19V8C19 6.9 18.1 6 17 6H14.5V4.5Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 15C13.1046 15 14 14.1046 14 13C14 11.8954 13.1046 11 12 11C10.8954 11 10 11.8954 10 13C10 14.1046 10.8954 15 12 15Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 18H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Skin Routine Builder
                            </h3>
                            <p className="text-gray-600">
                                Create your personalized skincare routine based on your skin type, concerns, and lifestyle. Stay consistent with reminders and easy-to-follow steps.
                            </p>
                        </div>

                        {/* Card 2 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M9 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 7L12 10L16 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 12L12 15L16 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 17L12 20L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Glow Progress Tracker
                            </h3>
                            <p className="text-gray-600">
                                Track your journey with daily skin logs, before/after photos, and routine streaks to see how your skin improves over time.
                            </p>
                        </div>

                        {/* Card 3 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C7.02944 3 3 7.02944 3 12C3 16.9706 7.02944 21 12 21Z" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 9H9.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M15 9H15.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Skincare Community
                            </h3>
                            <p className="text-gray-600">
                                Join a vibrant space of skincare lovers. Share your results, discover new routines, and get feedback from others like you.
                            </p>
                        </div>
                    </div>

                    {/* Second Row of Feature Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-8">
                        {/* Card 4 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M10 2L10 5" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 2L14 5" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M3 9H21" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M19 4H5C3.89543 4 3 4.89543 3 6V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V6C21 4.89543 20.1046 4 19 4Z" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8 13H8.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 13H12.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 13H16.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M8 17H8.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M12 17H12.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 17H16.01" stroke="#34495E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Follow Routines
                            </h3>
                            <p className="text-gray-600">
                                Stay on track with personalized routine reminders and notifications. Never miss a step in your skincare journey with timely alerts for morning and evening routines.
                            </p>
                        </div>

                        {/* Card 5 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M16 3.13C17.7699 3.58317 19.0078 5.17798 19.0078 7.005C19.0078 8.83202 17.7699 10.4268 16 10.88" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Creators Community
                            </h3>
                            <p className="text-gray-600">
                                Connect with skincare creators and influencers, follow their curated routines, and stay updated with their lifestyle tips. Easily link to their social profiles for more inspiration.
                            </p>
                        </div>

                        {/* Card 6 */}
                        <div className="bg-white rounded-3xl p-8 shadow-md transition-all will-change-transform">
                            <div className="w-16 h-16 rounded-full bg-[#FFD0C6] flex items-center justify-center mb-6">
                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#3c3c3c]">
                                    <path d="M12 8V12L14 14" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M3.05078 11.0002C3.27441 7.60011 5.89453 4.8101 9.17969 4.1701C12.4648 3.5301 15.7148 5.2401 17.0898 8.3101C18.4648 11.3901 17.5898 15.0601 14.9648 17.0001C12.3398 18.9401 8.63965 18.7401 6.21973 16.5001L3.05078 17.0002" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M20 10C21.1046 10 22 9.10457 22 8C22 6.89543 21.1046 6 20 6C18.8954 6 18 6.89543 18 8C18 9.10457 18.8954 10 20 10Z" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M20 6V3" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M18 4L20 6" stroke="#34495E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-semibold text-gray-800 mb-4">
                                Insights Dashboard
                            </h3>
                            <p className="text-gray-600">
                                Access comprehensive analytics as a creator to understand your audience demographics, routine popularity, and engagement metrics. Make data-driven decisions to grow your skincare community.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Problem Statement Section */}
            <div className="relative py-32 overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0 z-0">
                    <Image
                        src={GlowyProblemBg.src}
                        alt="Skincare background"
                        fill
                        className="object-cover"
                        priority
                    />
                    <div className="absolute inset-0 bg-white/30 backdrop-blur-[3px]"></div>
                </div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-16">
                        {/* Left side - Problem Statement */}
                        <div>
                            <div className="overflow-hidden">
                                <h2 className="text-5xl md:text-6xl font-bold text-[#34495E] mb-12">
                                    Problem Statement
                                </h2>
                            </div>

                            <div className="overflow-hidden">
                                <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-8">
                                    In today&apos;s saturated beauty market, consumers face <span className="font-semibold">skin confusion</span> and <span className="font-semibold">information overload</span>. With thousands of products and conflicting advice, many struggle to build routines that actually work for their unique skin needs, leading to wasted money and disappointing results.
                                </p>
                            </div>

                            <div className="overflow-hidden">
                                <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                                    Additionally, <span className="font-semibold">lack of consistency</span> and difficulty tracking progress prevent users from seeing real improvements. Without a structured approach and clear way to measure changes, skincare becomes a frustrating cycle of trial and error rather than an effective self-care practice.
                                </p>
                            </div>
                        </div>

                        {/* Right side - Mockup */}
                        <div className="relative">
                            {/* Phone mockup with image */}
                            <div className="relative mx-auto max-w-[320px]">
                                {/* Container for both iPhone frame and content */}
                                <div className="relative">
                                    {/* App screen image - positioned first so it's behind the frame */}
                                    <div className="relative aspect-[9/19.5] w-full">
                                        <Image
                                            src={GlowyProblemMockup.src}
                                            alt="Glowy App Routine Builder Screen"
                                            fill
                                            className="object-contain rounded-[38px] p-[1px]"
                                            sizes="(max-width: 768px) 80vw, 320px"
                                            priority
                                        />
                                    </div>
                                </div>

                                {/* Shadow beneath phone */}
                                <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 w-[70%] h-[20px] bg-black/15 blur-xl rounded-full"></div>
                            </div>

                            {/* Decorative elements */}
                            <div className="absolute top-[10%] right-[5%] w-20 h-20 rounded-full bg-[#FFD0C6]/30 blur-xl"></div>
                            <div className="absolute bottom-[15%] left-[10%] w-16 h-16 rounded-full bg-[#FFD0C6]/20 blur-lg"></div>
                        </div>
                    </div>

                    {/* Large Square Image Cards Row - Below entire section */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {/* Card 1 */}
                        <div className="">
                            <div className="rounded-3xl shadow-sm overflow-hidden relative transition-all duration-300 group-hover:shadow-xl h-full">
                                <div className="w-full h-[250px] sm:h-[250px] md:h-[250px] lg:h-full relative">
                                    <Image
                                        src={GlowyClearSkin.src}
                                        alt="Lack of Clear Skin"
                                        fill
                                        className="object-cover"
                                    />
                                </div>
                                <div className="p-4 sm:p-6 absolute bottom-0 left-0 right-0 bg-white backdrop-blur-lg border-t border-white">
                                    <h3 className="text-center font-bold text-lg sm:text-xl text-[#34495E]">Lack of Clear Skin</h3>
                                </div>
                            </div>
                        </div>

                        {/* Card 2 */}
                        <div className="">
                            <div className="rounded-3xl shadow-sm overflow-hidden relative transition-all duration-300 group-hover:shadow-xl h-full">
                                <div className="w-full h-[250px] sm:h-[250px] md:h-[250px] lg:h-full relative">
                                    <Image
                                        src={GlowyInconsistentRoutine.src}
                                        alt="Inconsistent Routines"
                                        fill
                                        className="object-cover"
                                    />
                                </div>
                                <div className="p-4 sm:p-6 absolute bottom-0 left-0 right-0 bg-white backdrop-blur-lg border-t border-white">
                                    <h3 className="text-center font-bold text-lg sm:text-xl text-[#34495E]">Inconsistent Routines</h3>
                                </div>
                            </div>
                        </div>

                        {/* Card 3 */}
                        <div className="">
                            <div className="rounded-3xl shadow-sm overflow-hidden relative transition-all duration-300 group-hover:shadow-xl h-full">
                                <div className="w-full h-[250px] sm:h-[250px] md:h-[250px] lg:h-full relative">
                                    <Image
                                        src={GlowyForAllSkin.src}
                                        alt="Effective for Different Skin Types"
                                        fill
                                        className="object-cover"
                                    />
                                </div>
                                <div className="p-4 sm:p-6 absolute bottom-0 left-0 right-0 bg-white backdrop-blur-lg border-t border-white">
                                    <h3 className="text-center font-bold text-lg sm:text-xl text-[#34495E]">For All Skin Types</h3>
                                </div>
                            </div>
                        </div>

                        {/* Card 4 - Text-based Progress Tracking */}
                        <div className="group">
                            <div className="rounded-3xl overflow-hidden bg-[#FFF0F7] transition-all duration-300 group-hover:shadow-xl h-full">
                                <div className="bg-white p-4 sm:p-6">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-pink-100 flex items-center justify-center mb-4 sm:mb-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 sm:h-6 sm:w-6 text-pink-600" viewBox="0 0 20 20" fill="currentColor">
                                            <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z" />
                                            <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z" />
                                        </svg>
                                    </div>
                                    <h3 className="font-bold text-lg sm:text-xl text-[#34495E] mb-2 sm:mb-3">Progress Tracking</h3>
                                    <p className="text-sm sm:text-base text-gray-700">
                                        Users can&apos;t effectively monitor changes in their skin over time, making it difficult to:
                                    </p>
                                    <ul className="mt-2 sm:mt-3 space-y-1 sm:space-y-2">
                                        <li className="flex items-start">
                                            <span className="text-pink-500 mr-2 mt-1">•</span>
                                            <span className="text-sm sm:text-base text-gray-700">Identify which products are working</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-pink-500 mr-2 mt-1">•</span>
                                            <span className="text-sm sm:text-base text-gray-700">Stay motivated with visible results</span>
                                        </li>
                                        <li className="flex items-start">
                                            <span className="text-pink-500 mr-2 mt-1">•</span>
                                            <span className="text-sm sm:text-base text-gray-700">Make informed adjustments to routines</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <section
                className="bg-[#F7F8FC] py-10 md:py-14"
                aria-labelledby="glowy-mid-cta-heading"
            >
                <div className="container mx-auto px-4 max-w-6xl text-center">
                    <h3
                        id="glowy-mid-cta-heading"
                        className="text-2xl md:text-3xl font-bold text-[#34495E] mb-3"
                    >
                        Got a similar app idea?
                    </h3>
                    <p className="text-lg text-[#3c3c4a] mb-6 max-w-xl mx-auto">
                        We build mobile apps from concept to launch.
                    </p>
                    <CommonButton href="/contact-us" variant="primary" size="lg">
                        Let&apos;s talk about your project →
                    </CommonButton>
                </div>
            </section>

            {/* Objectives & Research Approach Section */}
            <div className="bg-[#FFF3ED] py-32">
                <div className="container mx-auto px-4 max-w-7xl">
                    {/* Section Title */}
                    <div className="mb-16">
                        <div className="overflow-hidden">
                            <h2 className="text-5xl md:text-6xl font-bold text-[#34495E] leading-tight">
                                Objectives &<br />
                                research approach
                            </h2>
                        </div>
                    </div>

                    {/* Content - Full Width */}
                    <div className="mb-16">
                        <div className="overflow-hidden">
                            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-4xl">
                                At Devkrest, our design team set out to create an intuitive, personalized skincare experience that makes <span className="font-semibold">self-care accessible for everyone</span>. Our focus was on removing common barriers like lack of time, motivation, and overwhelming routines — and instead, building a product that encourages long-term skincare habits.
                            </p>
                        </div>

                        <div className="overflow-hidden">
                            <p className="text-lg md:text-xl text-gray-700 leading-relaxed max-w-4xl mt-8">
                                Guided by the <span className="font-semibold">Design Thinking methodology</span>, we conducted user interviews and surveys to deeply understand user pain points. This helped us craft a human-centered solution that balances functionality, beauty, and emotional engagement at every touchpoint.
                            </p>
                        </div>
                    </div>

                    {/* Design Thinking Process Component */}
                    <DesignThinkingProcess />
                </div>
            </div>

            {/* Devkrest's Role Section - Modified with six cards */}
            <div className="relative py-16 md:py-24 lg:py-32 bg-[#EFF1F9] overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0 opacity-30">
                    <div className="absolute top-[10%] right-[10%] w-[300px] h-[300px] rounded-full bg-blue-100/20 blur-[80px]"></div>
                    <div className="absolute bottom-[10%] left-[10%] w-[250px] h-[250px] rounded-full bg-purple-100/20 blur-[60px]"></div>
                </div>

                <div className="container mx-auto px-4 sm:px-6 max-w-7xl relative z-10">
                    {/* Header with title and description in row - left aligned */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-12 md:mb-16">
                        <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-[#3D4C6F] leading-tight mb-6 md:mb-0 md:max-w-xl">
                            Devkrest&apos;s Contribution <br className="hidden sm:block" />to Glowy
                        </h2>
                        <p className="text-base md:text-lg text-gray-700 md:max-w-xl">
                            At Devkrest, we partnered with Glowy to transform their skincare app concept into a fully-functional, user-focused reality. Our comprehensive approach encompassed strategy, design, development, and testing to create an app that truly resonates with users.
                        </p>
                    </div>

                    {/* Six Service Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                        {/* 1. Consulting Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">Consulting</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Strategic app planning</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Feature prioritization</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Technology stack selection</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-blue-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Business model validation</span>
                                </li>
                            </ul>
                        </div>

                        {/* 2. Research & Ideas Sharing Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-purple-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-purple-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">Research & Ideas Sharing</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-purple-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Market trend analysis</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-purple-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Collaborative ideation sessions</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-purple-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Innovation workshops</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-purple-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Feature brainstorming</span>
                                </li>
                            </ul>
                        </div>

                        {/* 3. Competitive and User Analysis Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-green-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 0l-2 2a1 1 0 101.414 1.414L8 10.414l1.293 1.293a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">Competitive & User Analysis</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-green-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Competitor feature mapping</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">User interviews & surveys</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Persona development</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-green-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">User journey mapping</span>
                                </li>
                            </ul>
                        </div>

                        {/* 4. Design & Prototyping Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-pink-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-pink-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">Design & Prototyping</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-pink-500 mr-2.5 block min-w-[8px]">•</span>
                                    <Link
                                        href="/web-development-services"
                                        className="text-gray-700 underline-offset-2 hover:underline hover:text-[#0055CC]"
                                    >
                                        UI/UX design
                                    </Link>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-pink-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Interactive prototypes</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-pink-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Design system creation</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-pink-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">User flow optimization</span>
                                </li>
                            </ul>
                        </div>

                        {/* 5. App & Admin Development Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-amber-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">App & Admin Development</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-amber-500 mr-2.5 block min-w-[8px]">•</span>
                                    <Link
                                        href="/mobile-app-development-services"
                                        className="text-gray-700 underline-offset-2 hover:underline hover:text-[#0055CC]"
                                    >
                                        Flutter mobile app
                                    </Link>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-amber-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Custom admin dashboard</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-amber-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">API development</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-amber-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Cloud infrastructure setup</span>
                                </li>
                            </ul>
                        </div>

                        {/* 6. Testing Card */}
                        <div className="bg-white rounded-xl p-6 md:p-8 shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1 will-change-transform">
                            <div className="w-14 h-14 rounded-full bg-teal-50 flex items-center justify-center mb-6">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7 text-teal-600" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <h3 className="text-lg md:text-xl font-semibold text-[#34495E] mb-4">Testing</h3>
                            <ul className="space-y-3">
                                <li className="flex items-start">
                                    <span className="text-teal-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">User acceptance testing</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-teal-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Performance optimization</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-teal-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Cross-device compatibility</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-teal-500 mr-2.5 block min-w-[8px]">•</span>
                                    <span className="text-gray-700">Security assessment</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Creation Process Section */}
            <div className="relative py-32 overflow-hidden">
                {/* Background Elements */}
                <div className="absolute inset-0">
                    <Image
                        src={GlowyDesignBg}
                        alt="Glowy app creation process background"
                        fill
                        className="object-cover object-center"
                        priority
                    />
                </div>
                <div className='absolute inset-0 bg-black/30'></div>
                <div className="container mx-auto px-4 max-w-7xl relative z-10">
                    {/* Section Title */}
                    <div className="mb-20 max-w-lg">
                        <h2 className="text-6xl md:text-7xl font-bold text-white leading-tight">
                            <div className="sm:hidden">
                                {/* Mobile-only version with better text breaking */}
                                <span className="inline-block text-white pr-1">
                                    Creation
                                </span>
                                <br />
                                <span className="inline-block text-white pr-1">
                                    Process
                                </span>
                            </div>

                            <div className="hidden sm:block">
                                {/* Tablet and desktop version */}
                                <span className="inline-block  text-white pr-2">
                                    Creation{" "}
                                </span>
                                <br />
                                <span className="inline-block text-white pr-2">
                                    Process
                                </span>
                            </div>
                        </h2>
                    </div>

                    {/* Process Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {/* Add hover animations using Framer Motion */}
                        <style jsx global>{`
                            .process-card {
                                transform-origin: center bottom;
                            }
                            .process-card:hover {
                                transform: translateY(-8px);
                                box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
                            }
                        `}</style>
                        {/* Research Card */}
                        <div className="process-card bg-white/15 backdrop-blur-md rounded-3xl p-8 border border-white/25 hover:bg-white/25 transition-all duration-500">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 rounded-full bg-[#34495E] flex items-center justify-center text-white text-lg font-medium">
                                    01
                                </div>
                                <h3 className="text-2xl font-bold text-white ml-4">Research</h3>
                            </div>
                            <ul className="space-y-3 text-white/90">
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>User audience</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>User interview</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Competitive analysis</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Functional analysis</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Mood board</span>
                                </li>
                            </ul>
                        </div>

                        {/* UI Design Card */}
                        <div className="process-card bg-white/15 backdrop-blur-md rounded-3xl p-8 border border-white/25 hover:bg-white/25 transition-all duration-500">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 rounded-full bg-[#34495E] flex items-center justify-center text-white text-lg font-medium">
                                    02
                                </div>
                                <h3 className="text-2xl font-bold text-white ml-4">UI Design</h3>
                            </div>
                            <ul className="space-y-3 text-white/90">
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Wireframes</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Visual concept</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Design system</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Components library</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Interactive prototype</span>
                                </li>
                            </ul>
                        </div>

                        {/* Development Card */}
                        <div className="process-card bg-white/15 backdrop-blur-md rounded-3xl p-8 border border-white/25 hover:bg-white/25 transition-all duration-500">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 rounded-full bg-[#34495E] flex items-center justify-center text-white text-lg font-medium">
                                    03
                                </div>
                                <h3 className="text-2xl font-bold text-white ml-4">Development</h3>
                            </div>
                            <ul className="space-y-3 text-white/90">
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Frontend implementation</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Backend integration</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>API development</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Code optimization</span>
                                </li>
                            </ul>
                        </div>

                        {/* Testing Card */}
                        <div className="process-card bg-white/15 backdrop-blur-md rounded-3xl p-8 border border-white/25 hover:bg-white/25 transition-all duration-500">
                            <div className="flex items-center mb-6">
                                <div className="w-12 h-12 rounded-full bg-[#34495E] flex items-center justify-center text-white text-lg font-medium">
                                    04
                                </div>
                                <h3 className="text-2xl font-bold text-white ml-4">Testing</h3>
                            </div>
                            <ul className="space-y-3 text-white/90">
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>User testing</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="text-white mr-2">•</span>
                                    <span>Final corrections</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>

            {/* Project Timeline Section */}
            <div className="bg-white py-16 md:py-24 lg:py-32 overflow-x-hidden">
                <div className="container mx-auto px-4 sm:px-6 max-w-7xl">
                    {/* Section Header */}
                    <div className="flex flex-col items-center justify-center mb-10 md:mb-16 lg:mb-20">
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-center text-gray-900 mb-3">Project Timeline</h2>
                        <p className="text-base md:text-lg text-gray-700 md:max-w-xl text-center">
                            A structured approach to designing and developing the Glowy app, ensuring timely delivery and quality results.
                        </p>
                    </div>

                    {/* Timeline Content - Desktop Version (hidden on mobile and tablet) */}
                    <div className="hidden lg:block relative">
                        {/* Vertical Grid Lines */}
                        <div className="absolute inset-0 grid grid-cols-8 h-full pointer-events-none">
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div className="border-r border-dashed border-neutral-200 h-full"></div>
                            <div></div>
                        </div>

                        {/* Week Headers */}
                        <div className="grid grid-cols-8 mb-6">
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 01</div>
                                <div className="text-xs text-gray-500">March 01</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 02</div>
                                <div className="text-xs text-gray-500">March 08</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 03</div>
                                <div className="text-xs text-gray-500">March 15</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 04</div>
                                <div className="text-xs text-gray-500">March 22</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 05</div>
                                <div className="text-xs text-gray-500">March 29</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 06</div>
                                <div className="text-xs text-gray-500">April 05</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 07</div>
                                <div className="text-xs text-gray-500">April 12</div>
                            </div>
                            <div className="px-2 text-center">
                                <div className="font-semibold text-sm">Week 08</div>
                                <div className="text-xs text-gray-500">April 19</div>
                            </div>
                        </div>

                        {/* Timeline Content */}
                        <div className="relative min-h-[600px]">
                            {/* Understand Card */}
                            <TimelineCard
                                color="bg-blue-500"
                                title="Understand"
                                description="User & Brand"
                                position="absolute left-[3%] top-[50px]"
                            />

                            {/* Research Card */}
                            <TimelineCard
                                color="bg-purple-500"
                                title="Research"
                                description="Interview, Focus Group Discussion, Surveys"
                                position="absolute left-[15%] top-[180px]"
                            />

                            {/* Analyze Card */}
                            <TimelineCard
                                color="bg-green-500"
                                title="Analyze"
                                description="User Journey Map & Personas"
                                position="absolute left-[27%] top-[50px]"
                            />

                            {/* Design Card */}
                            <TimelineCard
                                color="bg-[#F97E63]"
                                title="Design"
                                description="Sketch, Wireframe, Style guide"
                                position="absolute left-[40%] top-[180px]"
                            />

                            {/* UI Design Card */}
                            <TimelineCard
                                color="bg-[#F97E63]"
                                title="UI Design"
                                description="Visual Design & Components"
                                position="absolute left-[52%] top-[50px]"
                            />

                            {/* Development Card */}
                            <TimelineCard
                                color="bg-amber-400"
                                title="Development"
                                description="Frontend & Backend Implementation"
                                position="absolute left-[65%] top-[180px]"
                            />

                            {/* Testing Card */}
                            <TimelineCard
                                color="bg-purple-500"
                                title="Testing"
                                description="Usability Testing & Refinement"
                                position="absolute left-[77%] top-[50px]"
                            />

                            {/* Launch Card */}
                            <TimelineCard
                                color="bg-gray-900"
                                title="Launch"
                                description="Check-ins & Feedback"
                                position="absolute left-[90%] top-[180px]"
                            />

                            {/* Straight Vertical Connecting Lines - Behind Cards */}
                            <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
                                {/* Week 1 Vertical Line */}
                                <div className="absolute left-[6.25%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 2 Vertical Line */}
                                <div className="absolute left-[18.75%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 3 Vertical Line */}
                                <div className="absolute left-[31.25%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 4 Vertical Line */}
                                <div className="absolute left-[43.75%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 5 Vertical Line */}
                                <div className="absolute left-[56.25%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 6 Vertical Line */}
                                <div className="absolute left-[68.75%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 7 Vertical Line */}
                                <div className="absolute left-[81.25%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>

                                {/* Week 8 Vertical Line */}
                                <div className="absolute left-[93.75%] top-0 bottom-0 border-l-2 border-dashed border-gray-200"></div>
                            </div>
                        </div>
                    </div>

                    {/* Mobile Timeline - Vertical (visible only on mobile) */}
                    <div className="md:hidden">
                        <div className="relative pb-4 px-4">
                            {/* Timeline Dots and Content */}
                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-blue-200 border-2 border-white flex-shrink-0">1</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Understand</h3>
                                        <p className="text-gray-600 mb-3 text-sm">User & Brand</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-blue-50 text-blue-600 font-medium px-3 py-1 rounded-full inline-block">Week 1</div>
                                            <div className="text-xs text-gray-500">March 01</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-purple-200 border-2 border-white flex-shrink-0">2</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Research</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Interview, Focus Group Discussion, Surveys</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-purple-50 text-purple-600 font-medium px-3 py-1 rounded-full inline-block">Week 2</div>
                                            <div className="text-xs text-gray-500">March 08</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-green-200 border-2 border-white flex-shrink-0">3</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Analyze</h3>
                                        <p className="text-gray-600 mb-3 text-sm">User Journey Map & Personas</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-green-50 text-green-600 font-medium px-3 py-1 rounded-full inline-block">Week 3</div>
                                            <div className="text-xs text-gray-500">March 15</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#FF8B75] flex items-center justify-center text-white text-lg font-bold shadow-md shadow-orange-200 border-2 border-white flex-shrink-0">4</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Design</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Sketch, Wireframe, Style guide</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-orange-50 text-[#FF8B75] font-medium px-3 py-1 rounded-full inline-block">Week 4</div>
                                            <div className="text-xs text-gray-500">March 22</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#FF8B75] flex items-center justify-center text-white text-lg font-bold shadow-md shadow-orange-200 border-2 border-white flex-shrink-0">5</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">UI Design</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Visual Design & Components</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-orange-50 text-[#FF8B75] font-medium px-3 py-1 rounded-full inline-block">Week 5</div>
                                            <div className="text-xs text-gray-500">March 29</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-amber-200 border-2 border-white flex-shrink-0">6</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Development</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Frontend & Backend Implementation</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-amber-50 text-amber-600 font-medium px-3 py-1 rounded-full inline-block">Week 6</div>
                                            <div className="text-xs text-gray-500">April 05</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-purple-200 border-2 border-white flex-shrink-0">7</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Testing</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Usability Testing & Refinement</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-purple-50 text-purple-600 font-medium px-3 py-1 rounded-full inline-block">Week 7</div>
                                            <div className="text-xs text-gray-500">April 12</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="relative">
                                <div className="flex items-start gap-4">
                                    <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white text-lg font-bold shadow-md shadow-gray-300 border-2 border-white flex-shrink-0">8</div>
                                    <div className="bg-white p-5 rounded-3xl shadow-md border border-gray-100 hover:shadow-lg transition-all duration-300 w-full">
                                        <h3 className="text-xl font-bold mb-2 text-[#2C3A59]">Launch</h3>
                                        <p className="text-gray-600 mb-3 text-sm">Check-ins & Feedback</p>
                                        <div className="flex items-center justify-between">
                                            <div className="text-xs bg-gray-100 text-gray-700 font-medium px-3 py-1 rounded-full inline-block">Week 8</div>
                                            <div className="text-xs text-gray-500">April 19</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tablet Timeline - Horizontal Scrollable (visible only on tablet) */}
                    <div className="hidden md:block lg:hidden">
                        <div className="relative overflow-x-auto pb-8">
                            <div className="flex space-x-6 min-w-[1000px] px-4">
                                {/* Timeline Item 1 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-blue-500 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold mb-4">1</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Understand</h3>
                                        <p className="text-gray-600 text-sm mb-3">User & Brand</p>
                                        <div className="text-xs text-gray-500">March 01</div>
                                    </div>
                                </div>

                                {/* Timeline Item 2 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-purple-500 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold mb-4">2</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Research</h3>
                                        <p className="text-gray-600 text-sm mb-3">Interview, Focus Group Discussion, Surveys</p>
                                        <div className="text-xs text-gray-500">March 08</div>
                                    </div>
                                </div>

                                {/* Timeline Item 3 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-green-500 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white font-bold mb-4">3</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Analyze</h3>
                                        <p className="text-gray-600 text-sm mb-3">User Journey Map & Personas</p>
                                        <div className="text-xs text-gray-500">March 15</div>
                                    </div>
                                </div>

                                {/* Timeline Item 4 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-[#FF8B75] mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-[#FF8B75] flex items-center justify-center text-white font-bold mb-4">4</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Design</h3>
                                        <p className="text-gray-600 text-sm mb-3">Sketch, Wireframe, Style guide</p>
                                        <div className="text-xs text-gray-500">March 22</div>
                                    </div>
                                </div>

                                {/* Timeline Item 5 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-[#FF8B75] mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-[#FF8B75] flex items-center justify-center text-white font-bold mb-4">5</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">UI Design</h3>
                                        <p className="text-gray-600 text-sm mb-3">Visual Design & Components</p>
                                        <div className="text-xs text-gray-500">March 29</div>
                                    </div>
                                </div>

                                {/* Timeline Item 6 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-amber-400 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-amber-400 flex items-center justify-center text-white font-bold mb-4">6</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Development</h3>
                                        <p className="text-gray-600 text-sm mb-3">Frontend & Backend Implementation</p>
                                        <div className="text-xs text-gray-500">April 05</div>
                                    </div>
                                </div>

                                {/* Timeline Item 7 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-purple-500 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold mb-4">7</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Testing</h3>
                                        <p className="text-gray-600 text-sm mb-3">Usability Testing & Refinement</p>
                                        <div className="text-xs text-gray-500">April 12</div>
                                    </div>
                                </div>

                                {/* Timeline Item 8 */}
                                <div className="w-[200px] flex-shrink-0">
                                    <div className="h-1 bg-gray-900 mb-4"></div>
                                    <div className="bg-white p-5 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100">
                                        <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-bold mb-4">8</div>
                                        <h3 className="text-lg font-bold mb-2 text-[#2C3A59]">Launch</h3>
                                        <p className="text-gray-600 text-sm mb-3">Check-ins & Feedback</p>
                                        <div className="text-xs text-gray-500">April 19</div>
                                    </div>
                                </div>
                            </div>

                            {/* Scroll indicator */}
                            <div className="mt-6 text-center text-gray-500 text-sm flex items-center justify-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Scroll to see more
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative py-24 overflow-hidden bg-[#F5F8FF]">
                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    {/* Section Header */}
                    <div className="mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-[#2C3A59] mb-8">
                            Wireframes
                        </h2>

                        <div className="max-w-3xl">
                            <p className="text-[#4A5568] text-lg leading-relaxed">
                                Our wireframing process prioritizes
                                <span className="font-semibold"> user-centered design</span> by mapping out
                                interaction patterns and information architecture before aesthetics.
                                This foundational blueprint allowed us to validate navigation flows,
                                test usability assumptions, and refine core functionality with real users
                                early in the development cycle.
                            </p>
                        </div>
                    </div>

                    {/* Wireframe Images */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 will-change-transform">
                        {/* First Wireframe */}
                        <div className="relative">
                            <Image
                                src={GlowyWireframe1.src}
                                alt="Onboarding wireframe"
                                width={300}
                                height={600}
                                className="w-full h-auto"
                                onError={(e) => {
                                    // Fallback for missing image
                                    const target = e.target as HTMLElement;
                                    if (target.parentElement) {
                                        target.parentElement.classList.add("bg-gray-100");
                                        target.parentElement.innerHTML = `
                                            <div class="flex items-center justify-center h-full">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                    <polyline points="21 15 16 10 5 21"></polyline>
                                                </svg>
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        </div>

                        {/* Second Wireframe */}
                        <div className="relative">
                            <Image
                                src={GlowyWireframe2.src}
                                alt="Dashboard wireframe"
                                width={300}
                                height={600}
                                className="w-full h-auto"
                                onError={(e) => {
                                    // Fallback for missing image
                                    const target = e.target as HTMLElement;
                                    if (target.parentElement) {
                                        target.parentElement.classList.add("bg-gray-100");
                                        target.parentElement.innerHTML = `
                                            <div class="flex items-center justify-center h-full">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                    <polyline points="21 15 16 10 5 21"></polyline>
                                                </svg>
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        </div>

                        {/* Third Wireframe */}
                        <div className="relative">
                            <Image
                                src={GlowyWireframe3.src}
                                alt="Features wireframe"
                                width={300}
                                height={600}
                                className="w-full h-auto"
                                onError={(e) => {
                                    // Fallback for missing image
                                    const target = e.target as HTMLElement;
                                    if (target.parentElement) {
                                        target.parentElement.classList.add("bg-gray-100");
                                        target.parentElement.innerHTML = `
                                            <div class="flex items-center justify-center h-full">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="#CBD5E0" stroke-width="1" stroke-linecap="round" stroke-linejoin="round">
                                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                                    <polyline points="21 15 16 10 5 21"></polyline>
                                                </svg>
                                            </div>
                                        `;
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Typography & Styling Section - Same background as Wireframes */}
            <div className="relative py-24 overflow-hidden bg-[#F5F8FF] border-t border-gray-100">
                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    {/* Section Header */}
                    <div className="mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-[#23272F] mb-8">
                            Typography
                        </h2>

                        <div className="max-w-3xl">
                            <p className="text-[#4A5568] text-lg leading-relaxed">
                                Typography serves as the voice of our interface, balancing readability with personality.
                                We selected Poppins for its modern geometric structure and excellent legibility across devices,
                                creating a typographic system that conveys warmth while maintaining clarity for essential app interactions.
                            </p>
                        </div>
                    </div>

                    {/* Typography Content */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        {/* Left side - Font Family */}
                        <div className="relative will-change-transform">
                            {/* Large watermark text */}
                            <div className="absolute inset-0 flex items-center justify-center opacity-[0.05] pointer-events-none">
                                <span className="text-[14rem] font-bold text-[#23272F]">Poppins</span>
                            </div>

                            {/* Main font showcase */}
                            <div className="relative z-10">
                                <div className="mb-8">
                                    <h3 className="text-[#FF8B75] text-6xl md:text-8xl font-bold mb-2">Poppins</h3>
                                    <div className="flex items-center text-gray-500">
                                        <span className="text-xl">Typefamily by</span>
                                        <span className="text-xl font-medium ml-2">Indian Type Foundry</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right side - Font Weights */}
                        <div className="flex flex-col space-y-6 will-change-transform">
                            <div className="text-right">
                                <h3 className="text-5xl font-normal text-[#23272F]">Regular</h3>
                            </div>
                            <div className="text-right">
                                <h3 className="text-5xl font-medium text-[#23272F]">Medium</h3>
                            </div>
                            <div className="text-right">
                                <h3 className="text-5xl font-semibold text-[#23272F]">Semibold</h3>
                            </div>
                            <div className="text-right">
                                <h3 className="text-5xl font-bold text-[#23272F]">Bold</h3>
                            </div>
                        </div>
                    </div>

                    {/* Color Palette Section */}
                    <div className="mt-24 will-change-transform">
                        <h3 className="text-3xl font-bold text-[#23272F] mb-8">Color Palette</h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 overflow-hidden rounded-xl">
                            {/* Primary Color - Coral Blush */}
                            <div className="h-[300px] bg-[#FF8B75] relative p-8 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-white text-2xl font-medium">Coral Blush</h4>
                                    <p className="text-white/80 mt-1">#FF8B75</p>
                                </div>
                                <div className="text-white/90">
                                    <p>R: 255</p>
                                    <p>G: 139</p>
                                    <p>B: 117</p>
                                </div>

                                {/* Tints row */}
                                <div className="absolute bottom-0 left-0 right-0 flex h-[60px]">
                                    <div className="flex-1 bg-[#FF8B75]/90"></div>
                                    <div className="flex-1 bg-[#FF8B75]/70"></div>
                                    <div className="flex-1 bg-[#FF8B75]/50"></div>
                                    <div className="flex-1 bg-[#FF8B75]/30"></div>
                                </div>
                            </div>

                            {/* Dark Color - Midnight Slate */}
                            <div className="h-[300px] bg-[#23272F] relative p-8 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-white text-2xl font-medium">Midnight Slate</h4>
                                    <p className="text-white/80 mt-1">#23272F</p>
                                </div>
                                <div className="text-white/90">
                                    <p>R: 35</p>
                                    <p>G: 39</p>
                                    <p>B: 47</p>
                                </div>

                                {/* Tints row */}
                                <div className="absolute bottom-0 left-0 right-0 flex h-[60px]">
                                    <div className="flex-1 bg-[#23272F]/90"></div>
                                    <div className="flex-1 bg-[#23272F]/70"></div>
                                    <div className="flex-1 bg-[#23272F]/50"></div>
                                    <div className="flex-1 bg-[#23272F]/30"></div>
                                </div>
                            </div>

                            {/* Grey Color - Cloud Whisper */}
                            <div className="h-[300px] bg-[#F1F3F9] relative p-8 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-[#23272F] text-2xl font-medium">Cloud Whisper</h4>
                                    <p className="text-[#23272F]/80 mt-1">#F1F3F9</p>
                                </div>
                                <div className="text-[#23272F]/90">
                                    <p>R: 241</p>
                                    <p>G: 243</p>
                                    <p>B: 249</p>
                                </div>

                                {/* Tints row */}
                                <div className="absolute bottom-0 left-0 right-0 flex h-[60px]">
                                    <div className="flex-1 bg-[#F1F3F9]/90"></div>
                                    <div className="flex-1 bg-[#F1F3F9]/70"></div>
                                    <div className="flex-1 bg-[#F1F3F9]/50"></div>
                                    <div className="flex-1 bg-[#F1F3F9]/30"></div>
                                </div>
                            </div>

                            {/* Accent Color - Peach Mist */}
                            <div className="h-[300px] bg-[#FFEEEB] relative p-8 flex flex-col justify-between">
                                <div>
                                    <h4 className="text-[#23272F] text-2xl font-medium">Peach Mist</h4>
                                    <p className="text-[#23272F]/80 mt-1">#FFEEEB</p>
                                </div>
                                <div className="text-[#23272F]/90">
                                    <p>R: 255</p>
                                    <p>G: 238</p>
                                    <p>B: 235</p>
                                </div>

                                {/* Tints row */}
                                <div className="absolute bottom-0 left-0 right-0 flex h-[60px]">
                                    <div className="flex-1 bg-[#FFEEEB]/90"></div>
                                    <div className="flex-1 bg-[#FFEEEB]/70"></div>
                                    <div className="flex-1 bg-[#FFEEEB]/50"></div>
                                    <div className="flex-1 bg-[#FFEEEB]/30"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* The Final Product Section */}
            <div className="relative py-24 overflow-hidden">
                {/* Background with gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFEEEB] to-[#FFF5F3]"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[80px]"></div>
                <div className="absolute bottom-[10%] right-[5%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[60px]"></div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    <div className="flex flex-col gap-8">
                        {/* Title and Description */}
                        <div className="text-left">
                            <div className="mb-8">
                                <h2 className="text-4xl md:text-5xl font-bold text-[#2C3A59] mb-8">
                                    The final product
                                </h2>

                                <p className="text-[#7C8493] text-lg max-w-2xl">
                                    After multiple iterations and usability testing, the final design of
                                    Glowy delivers an intuitive and engaging experience. Below
                                    are the key sections of the app.
                                </p>
                            </div>
                        </div>

                        {/* Auth & Onboarding Section */}
                        <div className="flex flex-col gap-12">
                            {/* Section Title and Description */}
                            <div className="text-left">
                                {/* Decorative "01" element at the edge of screen */}
                                <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                    <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                        <div className=" left-0 bottom-5 h-[2px] w-[60px] bg-[#FF8B75]"></div>
                                        <div className=" left-[70px] top-[10px] text-[64px] font-bold text-[#FF8B75]">01</div>
                                    </div>
                                </div>

                                <div className="flex items-center mt-12 mb-3">
                                    <h3 className="text-2xl font-bold text-[#2C3A59]">Splash & Authentication</h3>
                                </div>
                                <p className="text-[#7C8493] max-w-3xl">
                                    The onboarding experience introduces users to Glowy&apos;s core features through a series of visually appealing screens.
                                    Each screen highlights a key benefit of the app, setting clear expectations and building excitement.
                                </p>
                            </div>

                            {/* Screenshots in Row */}
                            <div className="flex flex-col md:flex-row items-center justify-start gap-6 md:gap-8">
                                {/* First screen with iPhone frame */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-[280px] rounded-[40px] ">
                                        <div className="absolute top-0 left-0 right-0 h-[30px]">
                                            <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                        </div>
                                        <div className="rounded-3xl border-6 border-[#474747] shadow-lg overflow-hidden mx-[3px] mt-[3px] mb-[3px]">
                                            <Image
                                                src={GlowySplash.src}
                                                alt="Splash Screen"
                                                width={320}
                                                height={640}
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Second screen */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-[280px] rounded-[40px] ">
                                        <div className="absolute top-0 left-0 right-0 h-[30px]">
                                            <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                        </div>
                                        <div className="rounded-3xl  border-6 border-[#474747] shadow-lg overflow-hidden mx-[3px] mt-[3px] mb-[3px]">
                                            <Image
                                                src={GlowyLogin.src}
                                                alt="Login Screen"
                                                width={320}
                                                height={640}
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Third screen */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-[280px] rounded-[40px] ">
                                        <div className="absolute top-0 left-0 right-0 h-[30px]">
                                            <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                        </div>
                                        <div className="rounded-3xl  border-6 border-[#474747] shadow-lg overflow-hidden mx-[3px] mt-[3px] mb-[3px]">
                                            <Image
                                                src={GlowySignup.src}
                                                alt="Signup Screen"
                                                width={320}
                                                height={640}
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 02 Customized Skin Types Section */}
                        <div className="flex flex-col gap-12 mt-24">
                            {/* Decorative "01" element at the edge of screen */}

                            {/* Section Title and Description */}
                            <div className="text-left">
                                <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                    <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                        <div className="left-0 bottom-5 h-[2px] w-[60px] bg-[#FF8B75]"></div>
                                        <div className="left-[70px] top-[10px] text-[64px] font-bold text-[#FF8B75]">02</div>
                                    </div>
                                </div>
                                <div className="flex items-center mt-12 mb-3">
                                    <h3 className="text-2xl font-bold text-[#2C3A59]">Customized Skin Types</h3>
                                </div>
                                <p className="text-[#7C8493] max-w-3xl">
                                    Right in your own way — Glowy helps users identify their unique skin profile through a thoughtful questionnaire about skin types and concerns.
                                    This personalization process allows the app to tailor product recommendations and routines specifically to each user&apos;s needs,
                                    eliminating the guesswork that often leads to ineffective skincare choices.
                                </p>
                            </div>

                            {/* Screenshots Row - Responsive Layout */}
                            <div className="mt-12 mb-16">
                                <div className="flex flex-col items-center lg:items-start lg:flex-row lg:justify-between">
                                    {/* Phone Screenshots Container */}
                                    <div className="flex flex-col sm:flex-row justify-center items-center mb-10 lg:mb-0">
                                        {/* Left phone - Skin Type */}
                                        <div className="relative z-0 mb-6 sm:mb-0 sm:mr-[-80px] lg:mr-[-80px] will-change-transform">
                                            <div className="relative w-[280px] sm:w-[300px] shadow-lg rounded-3xl overflow-hidden">
                                                <Image
                                                    src={GlowySkintype}
                                                    alt="Skin Type Selection"
                                                    width={320}
                                                    height={640}
                                                    className="w-full h-auto"
                                                />
                                            </div>
                                        </div>

                                        {/* Right phone - Skin Concern */}
                                        <div className="relative z-10 will-change-transform">
                                            <div className="relative w-[280px] sm:w-[300px] shadow-lg rounded-3xl overflow-hidden">
                                                <Image
                                                    src={GlowySkinconcern}
                                                    alt="Skin Concerns Selection"
                                                    width={320}
                                                    height={640}
                                                    className="w-full h-auto"
                                                />
                                                <div className="absolute top-0 right-0 bottom-0 w-full sm:w-[320px] bg-gradient-to-r from-transparent to-white/60"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Creator Listing Phone */}
                                    <div className="relative z-10 will-change-transform lg:ml-auto">
                                        <div className="relative w-[280px] sm:w-[300px] object-contain overflow-hidden">
                                            <Image
                                                src={GlowyCreatorListing}
                                                alt="Creator Listing Screen"
                                                width={320}
                                                height={640}
                                                className="w-full h-auto"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 03 Home Screen */}
                        <div className="flex flex-col gap-12 mt-16">
                            {/* Decorative "01" element at the edge of screen */}

                            {/* Section Title and Description */}
                            <div className="text-left">
                                <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                    <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                        <div className=" left-0 bottom-5 h-[2px] w-[60px] bg-[#FF8B75]"></div>
                                        <div className=" left-[70px] top-[10px] text-[64px] font-bold text-[#FF8B75]">03</div>
                                    </div>
                                </div>
                                <div className="flex items-center mt-12 mb-3">
                                    <h3 className="text-2xl font-bold text-[#2C3A59]">Home Screen & Routine Details</h3>
                                </div>
                                <p className="text-[#7C8493] max-w-3xl">
                                    The Glowy home screen provides a personalized dashboard of subscribed skincare routines, making daily self-care effortless.
                                    Each routine includes detailed step-by-step instructions with precise timing for each product application, ensuring optimal absorption and effectiveness.
                                    Users can view which specific products to use for their skin type, complete with application techniques and recommended usage duration to maximize results.
                                </p>
                            </div>

                            {/* Screenshots in Row */}
                            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between relative mt-12 mb-16">
                                {/* Container for side-by-side phones with slight overlap */}
                                <div className="flex flex-col items-center justify-center mb-10 lg:mb-0">
                                    {/* First screen with iPhone frame */}
                                    <div className="relative will-change-transform">
                                        <div className="relative w-[300px] sm:w-[380px] rounded-[40px]">
                                            <div className="absolute top-0 left-0 right-0 h-[30px]">
                                                <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                            </div>
                                            <div className="rounded-3xl overflow-hidden">
                                                <Image
                                                    src={GlowyHomeScreen.src}
                                                    alt="Home Screen"
                                                    width={785}
                                                    height={484}
                                                    className="w-full h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Creator Listing Phone - Outside Container */}
                                <div className="flex justify-center lg:justify-end relative">
                                    <div className="relative will-change-transform">
                                        <div className="relative w-[300px] sm:w-[450px] lg:w-[680px] rounded-[40px]">
                                            <div className="absolute top-0 left-0 right-0 h-[30px]">
                                                <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                            </div>
                                            <div className="rounded-3xl overflow-hidden">
                                                <Image
                                                    src={GlowyHomeScreen2.src}
                                                    alt="Home Screen"
                                                    width={865}
                                                    height={492}
                                                    className="w-full h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>


            <div className="relative py-12 overflow-hidden">
                {/* Background with gradient */}
                <div className="absolute inset-0 bg-[#F5F8FF]"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[80px]"></div>
                <div className="absolute bottom-[10%] right-[5%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[60px]"></div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">


                    {/* 04 Add Your Routine Section*/}
                    <div className="flex flex-col gap-12 mt-16">
                        {/* Decorative "01" element at the edge of screen */}

                        {/* Section Title and Description */}
                        <div className="text-left">
                            <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                    <div className=" left-0 bottom-5 h-[2px] w-[60px] bg-[#3D4C6F]"></div>
                                    <div className=" left-[70px] top-[10px] text-[64px] font-bold text-[#3D4C6F]">04</div>
                                </div>
                            </div>
                            <div className="flex items-center mt-12 mb-3">
                                <h3 className="text-2xl font-bold text-[#2C3A59]">Add Private Routine</h3>
                            </div>
                            <p className="text-[#7C8493] max-w-3xl">
                                Glowy makes creating personalized routines simple and intuitive. Users can select products from their collection, set specific application times, and add detailed instructions for each step. The app provides smart recommendations for product sequencing based on active ingredients, helping users avoid conflicts between products while maximizing effectiveness. Routine creation includes options for morning and evening sequences with different products tailored to day and night skincare needs.
                            </p>
                        </div>

                        <div className="flex flex-col items-center justify-center relative mt-12 mb-16">
                            {/* Container for all four screens in a row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                                {/* First screen - Add Private Routine */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full border-6 border-[#474747] rounded-3xl shadow-lg overflow-hidden">
                                        <Image
                                            src={GlowyAddPrivateRoutine}
                                            alt="Add Private Routine Screen"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>

                                {/* Second screen - Add Private Routine Steps */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full border-6 border-[#474747] rounded-3xl shadow-lg overflow-hidden">
                                        <Image
                                            src={GlowyAddPrivateRoutineSteps}
                                            alt="Add Private Routine Steps Screen"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>

                                {/* Third screen - Add Private Routine Recur */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full border-6 border-[#474747] rounded-3xl shadow-lg overflow-hidden">
                                        <Image
                                            src={GlowyAddPrivateRoutineRecur}
                                            alt="Add Private Routine Recurrence Screen"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>

                                {/* Fourth screen - Edit Step */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full  border-6 border-[#474747] rounded-3xl shadow-lg overflow-hidden">
                                        <Image
                                            src={GlowyEditStep}
                                            alt="Edit Step Screen"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative py-12 overflow-hidden">
                {/* Background with gradient */}
                <div className="absolute inset-0 bg-white"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[80px]"></div>
                <div className="absolute bottom-[10%] right-[5%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[60px]"></div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">


                    {/* 04 Add Your Routine Section*/}
                    <div className="flex flex-col gap-12 mt-16">
                        {/* Decorative "01" element at the edge of screen */}

                        {/* Section Title and Description */}
                        <div className="text-left">
                            <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                    <div className=" left-0 bottom-5 h-[2px] w-[60px] bg-[#3D4C6F]"></div>
                                    <div className=" left-[70px] top-[10px] text-[64px] font-bold text-[#3D4C6F]">05</div>
                                </div>
                            </div>
                            <div className="flex items-center mt-12 mb-3">
                                <h3 className="text-2xl font-bold text-[#2C3A59]">Routine Insights</h3>
                            </div>
                            <p className="text-[#7C8493] max-w-3xl">
                                The Creator's Dashboard provides comprehensive analytics for skincare influencers and brands to track their routine performance. Creators can monitor key metrics like routine views, follower growth, and user engagement rates. The analytics section breaks down demographic data of users following their routines, while detailed reports highlight which products in routines receive the most interaction. Performance insights show peak usage times and completion rates, helping creators optimize their content for maximum effectiveness and engagement within the Glowy community.
                            </p>
                        </div>

                        <div className="flex flex-col items-center justify-center relative mt-12 mb-16">
                            {/* Container for all four screens in a row */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                                {/* First screen - Add Private Routine */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full rounded-3xl overflow-hidden">
                                        <Image
                                            src={GlowyRoutineActivity.src}
                                            alt="Glowy Routine Activity"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>

                                {/* Second screen - Add Private Routine Steps */}
                                <div className="relative will-change-transform">
                                    <div className="relative w-full rounded-3xl overflow-hidden">
                                        <Image
                                            src={GlowyCreatorStats.src}
                                            alt="Glowy Creator Stats"
                                            width={320}
                                            height={640}
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>

                                {/* Third screen - Add Private Routine Recur */}
                                <div className='flex flex-col gap-8'>
                                    <div className="relative will-change-transform">
                                        <div className="relative w-[280px] rounded-[40px] ">
                                            <div className="absolute top-0 left-0 right-0 h-[30px]">
                                                <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                            </div>
                                            <div className="rounded-lg overflow-hidden">
                                                <Image
                                                    src={GlowyAnalytics.src}
                                                    alt="Glowy Analytics"
                                                    width={785}
                                                    height={484}
                                                    className="w-full h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Fourth screen - Edit Step */}
                                    <div className="relative will-change-transform">
                                        <div className="relative w-[420px] rounded-[40px] ">
                                            <div className="absolute top-0 left-0 right-0 h-[30px]">
                                                <div className="absolute top-[10px] overflow-hidden left-1/2 transform -translate-x-1/2 w-[120px] h-[5px]"></div>
                                            </div>
                                            <div className="rounded-lg bg-gray-200 border-1 overflow-hidden">
                                                <Image
                                                    src={GlowyReports.src}
                                                    alt="Glowy Reports"
                                                    width={785}
                                                    height={484}
                                                    className="w-full h-auto"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="relative py-24 overflow-hidden">
                {/* Background with gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFEEEB] to-[#FFF5F3]"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[80px]"></div>
                <div className="absolute bottom-[10%] right-[5%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[60px]"></div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    <div className="flex flex-col gap-8">
                        {/* Auth & Onboarding Section */}
                        <div className="flex flex-col gap-12">
                            {/* Section Title and Description */}
                            <div className="text-left">
                                {/* Decorative "01" element at the edge of screen */}
                                <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                    <div className="w-full flex flex-row items-center justify-start gap-3 h-full">
                                        <div className=" left-0 bottom-5 h-[2px] w-[60px] bg-[#FF8B75]"></div>
                                        <div className=" left-[70px] top-[10px] text-[64px] font-bold text-[#FF8B75]">06</div>
                                    </div>
                                </div>

                                <div className="flex items-center mt-12 mb-3">
                                    <h3 className="text-2xl font-bold text-[#2C3A59]">More Screens</h3>
                                </div>
                                <p className="text-[#7C8493] max-w-3xl">
                                    Glowy offers a seamless experience for users to create personalized routines, track their progress, and engage with the Glowy community. The app provides a range of features to enhance the user&apos;s skincare journey, including routine creation, product tracking, and community engagement.
                                </p>
                            </div>
                            <div className="relative w-full h-[800px] overflow-hidden py-8">
                                {/* Gradient overlays for fading effect */}
                                <div className="absolute top-0 left-0 right-0 h-[80px] bg-gradient-to-b from-[#FFEEEB] to-transparent z-10"></div>
                                <div className="absolute bottom-0 left-0 right-0 h-[80px] bg-gradient-to-t from-[#FFEEEB] to-transparent z-10"></div>

                                {/* Auto-scrolling container */}
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    transition={{ duration: 1 }}
                                >
                                    {/* Auto-scrolling container */}
                                    <motion.div
                                        className="flex flex-col"
                                        initial={{ y: 0 }}
                                        animate={{ y: [0, -800] }}
                                        transition={{
                                            repeat: Infinity,
                                            repeatType: "loop",
                                            duration: 40,
                                            ease: "linear",
                                        }}
                                    >
                                        {/* First grid - original content */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-12">
                                            {/* First row */}
                                            <PhoneScreenshot
                                                src={GlowyScreen1.src}
                                                alt="More Screen 1"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen2.src}
                                                alt="More Screen 2"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen3.src}
                                                alt="More Screen 3"
                                            />

                                            {/* Second row */}
                                            <PhoneScreenshot
                                                src={GlowyScreen4.src}
                                                alt="More Screen 4"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen5.src}
                                                alt="More Screen 5"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen6.src}
                                                alt="More Screen 6"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen7.src}
                                                alt="More Screen 7"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen8.src}
                                                alt="More Screen 8"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen9.src}
                                                alt="More Screen 9"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen10.src}
                                                alt="More Screen 10"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen11.src}
                                                alt="More Screen 11"
                                            />
                                            <PhoneScreenshot
                                                src={GlowyScreen12.src}
                                                alt="More Screen 12"
                                            />
                                        </div>

                                        {/* Second grid - duplicated content for continuous scrolling */}
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                                            <PhoneScreenshot src={GlowyScreen1.src} alt="More Screen 1" />
                                            <PhoneScreenshot src={GlowyScreen2.src} alt="More Screen 2" />
                                            <PhoneScreenshot src={GlowyScreen3.src} alt="More Screen 3" />
                                            <PhoneScreenshot src={GlowyScreen4.src} alt="More Screen 4" />
                                            <PhoneScreenshot src={GlowyScreen5.src} alt="More Screen 5" />
                                            <PhoneScreenshot src={GlowyScreen6.src} alt="More Screen 6" />
                                            <PhoneScreenshot src={GlowyScreen7.src} alt="More Screen 7" />
                                            <PhoneScreenshot src={GlowyScreen8.src} alt="More Screen 8" />
                                            <PhoneScreenshot src={GlowyScreen9.src} alt="More Screen 9" />
                                            <PhoneScreenshot src={GlowyScreen10.src} alt="More Screen 10" />
                                            <PhoneScreenshot src={GlowyScreen11.src} alt="More Screen 11" />
                                            <PhoneScreenshot src={GlowyScreen12.src} alt="More Screen 12" />
                                        </div>
                                    </motion.div>
                                </motion.div>
                            </div>


                        </div>
                    </div>
                </div>
            </div>



            <div className="relative py-12 overflow-hidden">
                {/* Background with gradient */}
                <div className="absolute inset-0 bg-white"></div>

                {/* Subtle pattern overlay */}
                <div className="absolute inset-0 opacity-[0.03] bg-[radial-gradient(circle,_#FF8B75_1px,_transparent_1px)] bg-[length:20px_20px]"></div>

                {/* Decorative elements */}
                <div className="absolute top-[15%] left-[5%] w-[300px] h-[300px] rounded-full bg-[#FF8B75]/5 blur-[80px]"></div>
                <div className="absolute bottom-[10%] right-[5%] w-[250px] h-[250px] rounded-full bg-[#FF8B75]/10 blur-[60px]"></div>

                <div className="container mx-auto px-4 max-w-6xl relative z-10">


                    {/* 04 Add Your Routine Section*/}
                    <div className="flex flex-col gap-12 mt-16">
                        {/* Decorative "01" element at the edge of screen */}

                        {/* Section Title and Description */}
                        <div className="text-left">
                            <div className="-left-4 justify-center items-center sm:-left-8 md:-left-16 lg:-left-24 top-0 overflow-hidden">
                                <h2 className="text-4xl md:text-5xl font-bold text-[#2C3A59] mb-8">
                                    Client's Feedback
                                </h2>
                                <p className="text-[#7C8493] max-w-3xl">
                                    We are thrilled to share the feedback from our client, Tanvi Mehta, the Founder & CEO of Glowy. Tanvi is a skincare enthusiast and a visionary entrepreneur who is passionate about creating a seamless skincare experience for her users.
                                </p>
                            </div>
                        </div>
                        {/* Client's Feedback - Bold Modern Design */}
                        <div className="max-w-6xl mx-auto">
                            <div className="grid grid-cols-1 md:grid-cols-12 gap-0 border-1 border-gray-200 rounded-xl">
                                {/* Left column - Client photo and info */}
                                <div className="md:col-span-4 bg-[#ffb0a0] rounded-t-xl md:rounded-l-xl md:rounded-tr-none overflow-hidden relative">
                                    {/* Background pattern */}
                                    <div className="absolute inset-0 opacity-10 bg-[url('/images/case-studies/glowy/pattern-bg.png')] bg-cover mix-blend-overlay"></div>

                                    <div className="relative h-full flex flex-col items-center justify-center p-8 md:p-10 text-center">
                                        <div className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden border-4 border-white/30 shadow-lg mb-6">
                                            <Image
                                                src={GlowyTanvi}
                                                alt="Tanvi Mehta"
                                                width={128}
                                                height={128}
                                                className="w-full h-full object-cover"
                                                onError={(e) => {
                                                    const target = e.target as HTMLElement;
                                                    if (target.parentElement) {
                                                        target.parentElement.classList.add("bg-white/10");
                                                        target.parentElement.innerHTML = `<div class="flex items-center justify-center h-full text-white font-bold text-3xl">SJ</div>`;
                                                    }
                                                }}
                                            />
                                        </div>

                                        <h3 className="text-white text-xl md:text-2xl font-bold mb-1">Tanvi Mehta</h3>
                                        <p className="text-white/80 text-sm mb-6">Founder & CEO, Glowy</p>

                                        <div className="flex justify-center space-x-3">
                                            <div className="flex">
                                                {[...Array(5)].map((_, i) => (
                                                    <svg key={i} className="w-5 h-5 text-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                                    </svg>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right column - Testimonial */}
                                <div className="md:col-span-8 bg-white rounded-b-xl md:rounded-r-xl md:rounded-bl-none shadow-lg relative overflow-hidden">
                                    {/* Large decorative quote mark */}
                                    <div className="absolute top-6 right-8 opacity-10">
                                        <svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M9.13456 9H5.25654C4.98454 9 4.76254 9.223 4.76254 9.495V13.5C4.76254 13.777 4.98454 14 5.25654 14H7.51554V16.5C7.51554 18.43 5.94354 20 4.01154 20C3.73954 20 3.51654 20.224 3.51654 20.5V21.5C3.51654 21.776 3.73954 22 4.01154 22C7.04854 22 9.51554 19.538 9.51554 16.5V9.5C9.51554 9.223 9.29254 9 9.01954 9H9.13456Z" fill="currentColor" />
                                            <path d="M19.4856 9H15.6076C15.3356 9 15.1136 9.223 15.1136 9.495V13.5C15.1136 13.777 15.3356 14 15.6076 14H17.8666V16.5C17.8666 18.43 16.2946 20 14.3626 20C14.0906 20 13.8676 20.224 13.8676 20.5V21.5C13.8676 21.776 14.0906 22 14.3626 22C17.3996 22 19.8666 19.538 19.8666 16.5V9.5C19.8666 9.223 19.6446 9 19.3716 9H19.4856Z" fill="currentColor" />
                                        </svg>
                                    </div>

                                    <div className="p-8 md:p-12 lg:p-14">
                                        <div className="mb-8">
                                            <span className="inline-block bg-[#FF8B75]/10 text-[#FF8B75] text-sm font-medium px-3 py-1 rounded-full">Testimonial</span>
                                        </div>

                                        <blockquote className="text-gray-800 text-xl md:text-2xl font-normal leading-relaxed mb-10">
                                            "It has been a great experience working with Kaival and Lakhan. They understood the application well and the final product is amazing. The app is seamless, and the features are exactly what I needed."
                                        </blockquote>

                                        <p className="text-gray-600 mb-8">
                                            The product was delivered timely. My reviews and feedback of the application was also accommodated. It's evident that a lot of thought and effort went into making it user-friendly. Great job by the Devkrest team!
                                        </p>

                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div className="h-10 w-10 bg-[#FF8B75] rounded-full flex items-center justify-center text-white font-bold mr-3 md:hidden">
                                                    TM
                                                </div>
                                                <div className="md:hidden">
                                                    <h4 className="font-medium text-gray-900">Tanvi Mehta</h4>
                                                    <p className="text-sm text-gray-500">Founder & CEO</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center">
                                                <svg className="w-5 h-5 text-[#FF8B75] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                                    <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                                                </svg>
                                                <span className="text-gray-500 text-sm">Project completed 2023</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="mt-10 flex justify-center md:justify-start">
                            <Link
                                href="/case-studies"
                                className="text-[#FF8B75] font-semibold hover:underline inline-flex items-center gap-1"
                            >
                                ← See more case studies
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Communication: The Secret Sauce to Our Success */}
            <div className="relative py-24 overflow-hidden bg-gradient-to-b from-[#FFF6F8] to-[#FFF0F2]">
                <div className="container mx-auto px-4 max-w-6xl relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                        {/* Left column - Title, Description, and Points */}
                        <div>
                            {/* Title and Description */}
                            <div className="mb-12">
                                <h2 className="text-4xl max-w-2xl md:text-5xl font-bold text-[#2C3A59] mb-6">
                                    Communication: The Secret Sauce to Our Success
                                </h2>
                                <p className="text-[#7C8493] max-w-2xl">
                                    What made the Glowy app project shine? Our relentless commitment to transparent communication that kept everyone aligned and excited throughout the journey.
                                </p>
                            </div>

                            {/* Points */}
                            <div className="space-y-8">
                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF8B75] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M12 20h9"></path>
                                            <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">No Surprises, Just Progress</h3>
                                        <p className="text-gray-600">
                                            Daily updates via your preferred platform kept everyone informed, eliminating the anxiety of wondering "what's happening with my app today?"
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#8B9DC7] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="12" cy="12" r="10"></circle>
                                            <polyline points="12 6 12 12 16 14"></polyline>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Weekly Deep Dives That Matter</h3>
                                        <p className="text-gray-600">
                                            Our focused weekly meetings weren't just check-ins—they were collaborative strategy sessions that kept your vision at the center while driving momentum.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#FF8B75] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Problems Solved Before They Grew</h3>
                                        <p className="text-gray-600">
                                            Our team's proactive approach to challenges meant issues were identified and resolved early—keeping the project on track and your stress levels low.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#8B9DC7] flex items-center justify-center text-white mr-5">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                            <line x1="3" y1="9" x2="21" y2="9"></line>
                                            <line x1="9" y1="21" x2="9" y2="9"></line>
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-semibold text-[#2C3A59] mb-2">Full Transparency, Zero Guesswork</h3>
                                        <p className="text-gray-600">
                                            Our project tracking system gave you real-time visibility into every milestone, eliminating the black-box development experience that plagues most projects.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right side - Trello Card & Status Updates */}
                        <div className="relative space-y-2">
                            <div className="scale-75 mt-[-30px] overflow-hidden">
                                <Image
                                    src={TrelloCard.src}
                                    alt="Daily Status Updates Dashboard"
                                    width={300}
                                    height={100}
                                    className="w-auto h-auto object-cover"
                                />
                            </div>
                            {/* Daily Status Updates Image */}
                            <div className="rounded-2xl scale-90 mt-4 overflow-hidden">
                                <Image
                                    src={DailyStatusUpdates.src}
                                    alt="Daily Status Updates Dashboard"
                                    width={600}
                                    height={400}
                                    className="w-full h-auto object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Call to Action */}
            <BannerSection
                heading="Ready to build your skincare or wellness app?"
                sub_heading="We took Glowy from idea to launch in 8 weeks. Let's do the same for you."
                button_text="Start your project →"
                button_link="/contact-us"
            />
        </>
    );
}